import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarRange, Lock, Star } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { academicYearsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AcademicYear, AcademicYearStatus } from '@/types/domain';
import { formatDate } from '@/utils/format';

const schema = z
  .object({
    code: z.string().min(1, 'Code is required').max(32),
    name: z.string().min(1, 'Name is required').max(100),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
  })
  .refine((values) => values.endDate > values.startDate, {
    path: ['endDate'],
    message: 'End date must be after the start date',
  });

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = { code: '', name: '', startDate: '', endDate: '' };

/** Only forward transitions are offered — completing a year is deliberately one-way. */
const NEXT_STATUSES: Record<AcademicYearStatus, AcademicYearStatus[]> = {
  PLANNED: ['ACTIVE'],
  ACTIVE: ['COMPLETED'],
  COMPLETED: ['ARCHIVED'],
  ARCHIVED: [],
};

export function AcademicYearsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery('startDate,desc');

  const [statusFilter, setStatusFilter] = useState<AcademicYearStatus | ''>('');
  const [editing, setEditing] = useState<AcademicYear | null>(null);
  const [open, setOpen] = useState(false);
  const [activating, setActivating] = useState<AcademicYear | null>(null);
  const [deleting, setDeleting] = useState<AcademicYear | null>(null);
  const [transitioning, setTransitioning] = useState<{ year: AcademicYear; target: AcademicYearStatus } | null>(null);

  const params = { ...table.params, ...(statusFilter !== '' ? { status: statusFilter } : {}) };

  const query = useQuery({
    queryKey: queryKeys.academicYears.list(params),
    queryFn: () => academicYearsApi.list(params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.academicYears.all, queryKeys.gradeOfferings.all, queryKeys.sections.all],
    changeStatus: academicYearsApi.changeStatus,
    remove: academicYearsApi.remove,
    label: 'Academic year',
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      editing === null ? academicYearsApi.create(values) : academicYearsApi.update(editing.id, values),
    onSuccess: () => {
      toast.success(editing === null ? 'Academic year created' : 'Academic year updated');
      close();
      invalidateAll();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const setCurrent = useMutation({
    mutationFn: (year: AcademicYear) => academicYearsApi.setCurrent(year.id),
    onSuccess: (year) => {
      toast.success(`${year.name} is now the current academic year`);
      setActivating(null);
      invalidateAll();
    },
    onError: (error) => {
      handleApiError(error);
      setActivating(null);
    },
  });

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(year: AcademicYear) {
    setEditing(year);
    form.reset({
      code: year.code,
      name: year.name,
      startDate: year.startDate,
      endDate: year.endDate,
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<AcademicYear>[] = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      render: (year) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{year.name}</span>
          {year.current && (
            <Badge tone="accent">
              <Star className="h-3 w-3" aria-hidden="true" />
              current
            </Badge>
          )}
          {year.finished && (
            <Badge tone="neutral">
              <Lock className="h-3 w-3" aria-hidden="true" />
              read-only
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (year) => <span className="font-mono text-xs">{year.code}</span>,
    },
    {
      key: 'period',
      header: 'Period',
      sortKey: 'startDate',
      render: (year) => `${formatDate(year.startDate)} – ${formatDate(year.endDate)}`,
    },
    {
      key: 'offerings',
      header: 'Offerings',
      render: (year) => <span className="tabular-nums">{year.offeringCount}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (year) => <StatusBadge status={year.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (year) => {
        const nextStatuses = NEXT_STATUSES[year.status];
        return (
          <div className="flex justify-end gap-1">
            {can(PERMISSIONS.ACADEMIC_YEAR_SET_CURRENT) && !year.current && !year.finished && (
              <Button size="sm" variant="secondary" onClick={() => setActivating(year)}>
                Make current
              </Button>
            )}
            {can(PERMISSIONS.ACADEMIC_YEAR_UPDATE) && !year.finished && (
              <Button size="sm" variant="ghost" onClick={() => openEdit(year)}>
                Edit
              </Button>
            )}
            {can(PERMISSIONS.ACADEMIC_YEAR_UPDATE) && nextStatuses.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTransitioning({ year, target: nextStatuses[0] as AcademicYearStatus })}
              >
                Mark {nextStatuses[0]?.toLowerCase()}
              </Button>
            )}
            {can(PERMISSIONS.ACADEMIC_YEAR_DELETE) && !year.current && year.offeringCount === 0 && (
              <Button size="sm" variant="ghost" onClick={() => setDeleting(year)}>
                Delete
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ListPage
        title="Academic Years"
        description="Define the school's years. Exactly one may be current, and a finished year becomes read-only."
        icon={<CalendarRange className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.ACADEMIC_YEAR_CREATE) ? (
            <Button onClick={openCreate}>New academic year</Button>
          ) : undefined
        }
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search years…' }}
        filters={
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              table.setPage(0);
            }}
            options={[
              { value: 'PLANNED', label: 'Planned' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'ARCHIVED', label: 'Archived' },
            ]}
          />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(year) => year.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No academic years yet"
          emptyDescription="An academic year is required before grades can be offered or students enrolled."
          emptyIcon={<CalendarRange className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={open}
        title={editing === null ? 'New academic year' : `Edit ${editing.code}`}
        description="Years may not overlap — that would make 'which year is this date in' ambiguous."
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              hint="Unique within the school, e.g. 2026-27"
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField
              label="Name"
              required
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Start date"
              type="date"
              required
              error={form.formState.errors.startDate?.message}
              {...form.register('startDate')}
            />
            <TextField
              label="End date"
              type="date"
              required
              error={form.formState.errors.endDate?.message}
              {...form.register('endDate')}
            />
          </div>
          <p className="rounded-lg bg-surface-2 px-3 py-2 text-xs text-muted">
            New years start as <strong>Planned</strong>. Making one current also activates it.
          </p>
        </form>
      </Modal>

      <ConfirmDialog
        open={activating !== null}
        title="Change the current academic year"
        message={
          activating === null
            ? ''
            : `Make ${activating.name} the current year? ${
                query.data?.data.find((year) => year.current)?.name ?? 'The previous current year'
              } will no longer be current, and this changes the default context for enrolment and reporting across the school.`
        }
        confirmLabel="Make current"
        loading={setCurrent.isPending}
        onConfirm={() => activating !== null && setCurrent.mutate(activating)}
        onCancel={() => setActivating(null)}
      />

      <ConfirmDialog
        open={transitioning !== null}
        title={`Mark ${transitioning?.target.toLowerCase() ?? ''}`}
        message={
          transitioning === null
            ? ''
            : transitioning.target === 'COMPLETED'
              ? `Mark ${transitioning.year.name} as completed? Its grade offerings, sections and enrolments become read-only, and this cannot be undone.`
              : `Mark ${transitioning.year.name} as ${transitioning.target.toLowerCase()}?`
        }
        confirmLabel="Confirm"
        destructive={transitioning?.target === 'COMPLETED'}
        loading={statusMutation.isPending}
        onConfirm={() => {
          if (transitioning !== null) {
            statusMutation.mutate(
              { id: transitioning.year.id, payload: { status: transitioning.target } },
              { onSettled: () => setTransitioning(null) },
            );
          }
        }}
        onCancel={() => setTransitioning(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete academic year"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name}? This is refused while any grade offering exists for it.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
